import { Box, Stack, Typography } from "@mui/material";
import { alpha, PaletteColor } from "@mui/material/styles";
import { ReactNode } from "react";
import { useTheme } from "../../context/ThemeContext";

// icon tile: side in theme.spacing units, corners in theme.shape.borderRadius
// multiples, and how strongly a tint colour washes the tile
const TILE_SIZE_UNITS = 5.75;
const TILE_BORDER_RADIUS = 1.5;
const TINT_ALPHA = 0.15;

/**
 * The chrome shared by every "nothing to show here yet" panel: an icon on a
 * tinted tile, a bold title over a quiet intro, all in one centred column.
 *
 * The configuration sections, the analytics prerequisites and the settled
 * bargaining view each say this kind of thing from a different side of the
 * case, so they are built from the same pieces here rather than each carrying
 * its own copy that would drift.
 */

/**
 * The icon tile. Neutral by default; pass a palette colour when the state is a
 * result rather than an absence (a settled case is done, not missing), and the
 * tile takes a wash of it with the icon in its dark shade.
 */
export const EmptyStateIcon = ({
  icon,
  tint,
}: {
  icon: ReactNode;
  tint?: PaletteColor;
}) => {
  const { theme } = useTheme();

  return (
    <Box
      sx={{
        width: theme.spacing(TILE_SIZE_UNITS),
        height: theme.spacing(TILE_SIZE_UNITS),
        borderRadius: TILE_BORDER_RADIUS,
        bgcolor: tint
          ? alpha(tint.main, TINT_ALPHA)
          : theme.palette.action.hover,
        // the icon inherits this, so consumers pass a bare icon element
        color: tint ? tint.dark : theme.palette.text.secondary,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {icon}
    </Box>
  );
};

export const EmptyStateHeader = ({
  icon,
  tint,
  title,
  intro,
}: {
  icon: ReactNode;
  tint?: PaletteColor;
  title: string;
  intro?: string;
}) => {
  const { theme } = useTheme();

  return (
    <Stack
      sx={{
        alignItems: "center",
        gap: theme.spacing(2),
        textAlign: "center",
      }}
    >
      <EmptyStateIcon icon={icon} tint={tint} />

      <Typography
        variant="h6"
        sx={{
          fontWeight: "bold",
        }}
      >
        {title}
      </Typography>

      {intro && (
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            textWrap: "pretty",
          }}
        >
          {intro}
        </Typography>
      )}
    </Stack>
  );
};

/**
 * The centred column the pieces sit in. Children are centred, so anything that
 * should span the column (a progress bar, a list) says `width: "100%"` itself.
 * One gap governs every step down the panel, so the rhythm stays even.
 */
export const EmptyStateFrame = ({
  children,
  minHeight,
}: {
  children: ReactNode;
  /** Set when the state stands in for a whole area and should fill it. */
  minHeight?: string;
}) => {
  const { theme } = useTheme();

  return (
    <Stack
      sx={{
        alignItems: "center",
        justifyContent: "center",
        px: theme.spacing(2),
        py: theme.spacing(5),
        minHeight,
      }}
    >
      <Stack
        sx={{
          alignItems: "center",
          gap: theme.spacing(2),
          width: "100%",
          maxWidth: theme.spacing(70),
        }}
      >
        {children}
      </Stack>
    </Stack>
  );
};
